import type { LookupListeKodu, RoleScopeType, RoleType } from '@isg/shared-types';
import type {
  BelirlenmeYolu,
  BildirimTipi,
  SozlukListeSinifi,
  SozlukModulu,
} from '@isg/validation';
import {
  AKSIYON_DURUM_ETIKETI,
  INCIDENT_AKIS_ETIKETI,
  NONCONFORMITY_DURUM_ETIKETI,
  INCIDENT_ARASTIRMA_YONTEMI_ETIKETI,
  INCIDENT_EK_TIPI_ETIKETI,
  INCIDENT_INVESTIGATOR_ROL_ETIKETI,
  INCIDENT_KANIT_KATEGORISI_ETIKETI,
  INCIDENT_TARAF_ETIKETI,
  KIMLIK_TIPI_ETIKETI,
  PTW_DURUM_ETIKETI,
  PTW_ISLEM_ETIKETI,
  PTW_SUPER_DURUM_ETIKETI,
  SIDDET_SIRA_ETIKETI,
  EGITIM_SONUC_ETIKETI,
  EGITIM_TARAF_ETIKETI,
  EGITIM_YONTEM_ETIKETI,
  SAPKA_ETIKETI,
} from '@isg/validation';

/**
 * TR etiket sözlüğü (F1·S10). Enum token'ları İngilizce'dir ve ekrana ASLA
 * çıplak yazılmaz — görünen her etiket bu katmandan gelir (Teknoloji §10;
 * Kapsam §7.4). Web v1 TEK DİL (TR) — çok dillilik yalnız saha yüzeyinin
 * kararıdır; ikinci dil gelirse yalnız bu katman çoğalır.
 */

export const TEHLIKE_SINIFI_ETIKET: Record<string, string> = {
  low_risk: 'Az tehlikeli',
  hazardous: 'Tehlikeli',
  highly_hazardous: 'Çok tehlikeli',
};

export const ISYERI_DURUM_ETIKET: Record<string, string> = {
  active: 'Aktif',
  archived: 'Arşivde',
  suspended: 'Askıda',
};

/**
 * ✏️ **F4·S9: TEK KAYNAK `@isg/validation`DA.** Aynı etiketi sunucuda
 * üretilen Excel çıktısı da basıyor; kopya tutmak ekran ile dosyayı
 * ayrıştırırdı. Buradaki ad geriye dönük uyum için korunur.
 */
export const KIMLIK_TIPI_ETIKET: Record<string, string> = KIMLIK_TIPI_ETIKETI;

export const LOKASYON_TIP_ETIKET: Record<string, string> = {
  permanent: 'Kalıcı',
  temporary: 'Geçici',
};

/**
 * Toplu yükleme satır sonucu rozetleri (F1.5·S12). Portal yanıtında
 * restore_required hiç GELMEZ (sunucu 'error'a katlar — D2 portal dili);
 * 'Geri getirme gerekli' yalnız iç ekranda görünür.
 */
export const SATIR_SONUC_ETIKET: Record<string, string> = {
  added: 'Eklendi',
  new_employment: 'Yeni istihdam',
  unchanged: 'Değişiklik yok',
  restore_required: 'Geri getirme gerekli',
  error: 'İşlenemedi',
};

/** Aksiyon yaşam döngüsü etiketleri (F2·S2 — Kapsam §22.3/1). */
/** ✏️ F4·B2·2: tek kaynak @isg/validation (rapor PDF'i aynı kelimeyi basar). */
export const AKSIYON_DURUM_ETIKET: Record<string, string> = AKSIYON_DURUM_ETIKETI;

export const TALEP_DURUM_ETIKET: Record<string, string> = {
  open: 'Açık',
  converted: 'Aksiyona dönüştürüldü',
};

/** Zil kalem başlıkları (F2·S2 — enum token'ı ekrana çıkmaz). */
/**
 * ✏️ BULGU 103: anahtar kümesi `BildirimTipi` — altı tip etiketsizdi ve zil
 * HAM TOKEN basıyordu (`uygunsuzluk_bildirimi` · `ptw_sure_dolumu` …; D2).
 * `Record` artık yeni tipin etiketini derlemede ZORUNLU kılar.
 */
export const BILDIRIM_TIP_ETIKET: Readonly<Record<BildirimTipi, string>> = {
  evrak_gecerlilik: 'Yüklenici evrak süresi',
  ekipman_kontrol: 'Ekipman kontrol süresi',
  bekleyen_hesap: 'Bekleyen hesap',
  temsilci_daveti: 'Temsilci daveti',
  aktarma_sonucu: 'Aktarma sonucu',
  // TUR 6 (Ç2 · K3)
  evrak_karari: 'Yüklenici evrak kararı',
  sozlesme_bitis_vadesi: 'Yüklenici sözleşmesi bitiyor',
  aksiyon_atandi: 'Size aksiyon atandı',
  aksiyon_tamamlandi: 'Aksiyon tamamlandı bildirildi',
  aksiyon_kapandi: 'Aksiyon kapatıldı',
  aksiyon_geri_acildi: 'Aksiyon geri açıldı',
  aksiyon_termin: 'Aksiyon termini',
  aksiyon_termin_yaklasiyor: 'Aksiyon termini yaklaşıyor',
  uygunsuzluk_bildirimi: 'Yeni uygunsuzluk bildirimi',
  uygunsuzluk_hatirlatma: 'Değerlendirilmemiş uygunsuzluk',
  hedefsiz_atama: 'Hedefsiz görev ataması',
  ptw_sure_dolumu: 'İş izninin süresi doldu',
  ptw_devir_talebi: 'İş izni devir talebi',
  // F2·S4 — form motoru (Kapsam §22.5/8)
  ramak_kala_bildirimi: 'Yeni ramak kala bildirimi',
  sablon_guncellendi: 'Form şablonu güncellendi',
  sablon_gizlendi: 'Form şablonu gizlendi',
  // F3·S2 — PTW onay zinciri (Kapsam §16.7)
  ptw_onay_bekliyor: 'Onay bekleyen iş izni',
  ptw_onay_sonucu: 'İş izni onay kararı',
  // F3·S3 — PTW canlı kontrol (K7)
  ptw_uygunsuzluk: 'İş izninde uygunsuzluk',
  // F3B·S2 — olay modülü (U9)
  calisma_saati_duzeltme: 'Çalışma saati düzeltmesi',
  // F3B·S4 — bildirim yüzeyi (madde 64)
  olay_bildirimi: 'Yeni olay bildirimi',
  olay_durumu: 'Olay bildiriminizin durumu',
  // F4·S1 — vade bildirimleri (Kapsam §22.2/9): üç VADE + üç EKSİK.
  egitim_yetkinlik_vadesi: 'Eğitim/yetkinlik süresi',
  saglik_kanaat_vadesi: 'Sağlık kanaati süresi',
  risk_yenileme_vadesi: 'Risk değerlendirmesi yenileme',
  sgk_bildirim_vadesi: 'SGK iş kazası bildirimi',
  eksik_temel_isg: 'Temel İSG eğitimi eksik',
  eksik_saglik_kanaati: 'Sağlık kanaati eksik',
  // İSG kurulu (Kapsam §28 — 05.09.2026)
  kurul_toplanti_daveti: 'İSG kurulu toplantı daveti',
  kurul_karar_ilani: 'İSG kurulu kararları ilan edildi',
  kurul_kacinma_tebligi: 'İSG kurulu kaçınma hakkı tebliği',
  kurul_toplanti_vadesi: 'İSG kurulu toplantı vadesi',
  // Acil durum (Kapsam §29 — 06.09.2026)
  kontrollu_belge_vadesi: 'Acil durum belgesi yenileme vadesi',
  eksik_risk_degerlendirme: 'Risk değerlendirmesi yok',
  // TUR 8 / F1 — yükümlülük değerlendirmesinin yıllık vadesi.
  yukumluluk_gozden_gecirme_vadesi: 'Yasal uygunluk değerlendirmesi eskidi',
};

/** Olay modülü etiketleri (F3B·S2 — Kapsam §16.8; enum token ekrana çıkmaz). */
// ✏️ F4·B2·2: olay etiketlerinin TEK KAYNAĞI @isg/validation — araştırma
// raporu PDF'i (sunucuda üretilir) aynı kelimeyi bassın (KIMLIK_TIPI deseni).
// ✏️ T2·0 (K2): durum etiketi FONKSİYONDUR — `investigating` akışa göre
// Araştırmada/Değerlendirmede, türetilmiş kapalı "Kapandı"; ikisi de
// @isg/validation'dan (rapor aynı kelimeyi basar). Public takip yüzeyi
// akış almadığı için kendi nötr etiketini kullanır (K11 — "Sonuçlandı").
export { olayDurumEtiketi, publicTakipDurumEtiketi } from '@isg/validation';

/**
 * Olay listesi DURUM SÜZGECİ seçenekleri (T2·0): token değil GÖRÜNÜM
 * seçeneği — "Kapalı" türetilmiş alandan (`kapali`) süzülür, durum
 * token'ından değil; "Araştırmada / Değerlendirmede" tek token'dır
 * (`investigating`), etiket akıştan gelir. Red ayrı anahtardır (süzgeç).
 */
export const OLAY_SUZGEC_DURUM_ETIKET: Record<'reported' | 'investigating' | 'kapali', string> = {
  reported: 'Bildirildi',
  investigating: 'Araştırmada / Değerlendirmede',
  kapali: 'Kapandı',
};

export const OLAY_AKIS_ETIKET: Record<string, string> = INCIDENT_AKIS_ETIKETI;

/**
 * UYGUNSUZLUK DURUM ETİKETLERİ (Tur 2½a) — alias, ikinci metin DEĞİL:
 * kaynak `@isg/validation NONCONFORMITY_DURUM_ETIKETI`dir (rapor ve ekran
 * aynı kelimeyi kullansın).
 * ⚠️ *İşlemde* ve *Kapalı* burada YOK — ikisi TÜRETİLMİŞ etiketlerdir
 * (`uygunsuzlukDurumEtiketi`), token değil.
 */
export const UYGUNSUZLUK_DURUM_ETIKET: Record<string, string> = NONCONFORMITY_DURUM_ETIKETI;

/** Kaza dosyası ek tipleri (R13 — F3B·S5; DAR enum). */
export const OLAY_EK_TIPI_ETIKET: Record<string, string> = INCIDENT_EK_TIPI_ETIKETI;

/** Kanıt kategorisi + araştırma ekibi rolü (F4·B2·2 — madde 146). */
export const OLAY_KANIT_KATEGORISI_ETIKET: Record<string, string> =
  INCIDENT_KANIT_KATEGORISI_ETIKETI;
export const OLAY_ARASTIRMACI_ROL_ETIKET: Record<string, string> =
  INCIDENT_INVESTIGATOR_ROL_ETIKETI;

/** Çalışma saati içe aktarma satır sonuçları (F3B·S5). */
export const CALISMA_SAATI_IMPORT_ETIKET: Record<string, string> = {
  yeni: 'Eklendi',
  guncellendi: 'Güncellendi',
  hata: 'İşlenemedi',
};

export const OLAY_TARAF_ETIKET: Record<string, string> = INCIDENT_TARAF_ETIKETI;

/**
 * ARAŞTIRMA YÖNTEMİ (F4·B1 — O2). Enum'da İngilizce token, ekranda Türkçe
 * ad — şemaya görünen metin gömülmez (adlandırma konvansiyonu).
 */
/**
 * K1-K3 SAĞLIK SINIRI ŞERHİ (Kapsam §16.8/7-f.8). Metin EKRAN KOPYASIDIR;
 * HANGİ kategorilerde gösterileceği bir ÜRÜN KURALIDIR ve sunucudan gelir
 * (`kokSebepler[].saglikSiniri` — @isg/db SAGLIK_SINIRI_KATEGORI_KODLARI).
 * Ekran kategori kodlarının anlamını bilmez.
 */
export const SAGLIK_SINIRI_SERHI =
  'Bu kalemler araştırma bulgusudur. Tanı, tetkik değeri ya da hastalık adı yazmayın.';

export const OLAY_ARASTIRMA_YONTEMI_ETIKET: Record<string, string> =
  INCIDENT_ARASTIRMA_YONTEMI_ETIKETI;

/** Kişi şiddet merdiveni (O4 — ÜRÜN KİLİTLİ 6 basamak; kayda `sira` yazılır,
 *  ad GÖSTERİMDİR — sözlük D3 metinleriyle seed'e indi, F3B·S3). D1
 *  (17.08.2026, madde 58): 4. basamak "İş göremezlik / uzuv kaybı" —
 *  "sürekli" sıfatı SGK hukuki statü terimiyle karıştığı için düştü. */
export const SIDDET_SIRA_ETIKET: Record<number, string> = SIDDET_SIRA_ETIKETI;

/** Seviye türetme girdisi (O7/D4 — sınıf kuralının makine-okur anahtarı). */
export const TURETME_GIRDISI_ETIKET: Record<string, string> = {
  merdiven: 'Şiddet merdiveni',
  yayilim: 'Yayılım sınırı',
  hasar_tutari: 'Hasar tutarı (eşik)',
  esik_tablosu: 'Eşik tablosu (kayıp miktarı × madde sınıfı)',
};

/** Miktar birimi (çevre/proses sayı alanları — O7). */
export const MIKTAR_BIRIM_ETIKET: Record<string, string> = {
  kg: 'kg',
  lt: 'lt',
  m3: 'm³',
};

/**
 * Geçiş log'u işlem etiketleri. ✏️ T2·0 (K12): ESKİ işlemler (`incelemeye_al`
 * · `degerlendirmeye_al` · `kapat`) KALIR — tarihsel satırlar okunur; yeni
 * kayıt bunları üretmez. Akış başlatma TEK işlemdir (`arastirmaya_al`) ve
 * etiketi akıştan türer (`olayIslemEtiketi`): değerlendirme akışında
 * "Değerlendirme başlatıldı" okunur.
 */
export const OLAY_ISLEM_ETIKET: Record<string, string> = {
  incelemeye_al: 'İncelemeye alındı',
  reddet: 'Reddedildi',
  arastirmaya_al: 'Araştırma başlatıldı',
  degerlendirmeye_al: 'Değerlendirmeye alındı',
  arastirmaya_yukselt: 'Araştırmaya yükseltildi',
  kapat: 'Kapatıldı',
  tur_duzelt: 'Olay türü düzeltildi',
};

export function olayIslemEtiketi(islem: string, akis: string): string {
  if (islem === 'arastirmaya_al' && akis === 'degerlendirme') return 'Değerlendirme başlatıldı';
  return OLAY_ISLEM_ETIKET[islem] ?? islem;
}

/**
 * ✏️ **PTW ONAY DÜĞMESİ (14.09.2026 — K-62b): HARİTA YAZILMAZ, IMPORT EDİLİR**
 * (tur 10 / K7.1 deseni). Gövde `@isg/validation SAPKA_ETIKETI`ye taşındı —
 * sunucunun boş-havuz ret mesajı da aynı etiketi basar; iki kopya ayrışırdı.
 * Ad burada DURUR — ekranlar onu bu adla okur.
 */
export const SAPKA_ETIKET: Record<RoleType, string> = SAPKA_ETIKETI;

/**
 * ✏️ Gezinti artçısı 2 / B3 — YAZAMAMA CÜMLESİNİN ŞAPKA KISMI.
 * `['holding_admin', 'isg_specialist']` → *"holding yöneticisi veya İSG
 * uzmanı"*. Liste kapının KENDİ sabitinden verilir (ör.
 * `GOREVLENDIRME_YAZMA_SAPKALARI`): cümle elle yazılsaydı liste değiştiği gün
 * yalan söylerdi. Kısaltmayla başlayan etiket (İSG) küçültülmez.
 */
export function sapkaListesiMetni(sapkalar: readonly RoleType[]): string {
  const adlar = sapkalar.map((sapka) => {
    const etiket = SAPKA_ETIKET[sapka];
    // Kısaltma (İSG …) ve BAŞLIK biçimli etiket (✏️ 28.10.2026 DT-4: "Merkez
    // Yöneticisi" — her sözcüğü büyük harfli unvan) cümle içinde küçültülmez;
    // yalnız ilk sözcüğü büyük olan sıradan etiket küçülür ("şirket yöneticisi").
    return /^\p{Lu}\p{Lu}/u.test(etiket) || /\s\p{Lu}/u.test(etiket)
      ? etiket
      : etiket.charAt(0).toLocaleLowerCase('tr') + etiket.slice(1);
  });
  if (adlar.length <= 1) return adlar[0] ?? '';
  return `${adlar.slice(0, -1).join(', ')} veya ${adlar[adlar.length - 1]}`;
}

/**
 * BAHÇE KAPSAM TÜRLERİ (F4·B2 — Y1/Y2; ERD §3d dört kollu CHECK). Derinlik
 * kuralı burada DEĞİL `@isg/validation izinliBahceTurleri`dedir — bu tablo
 * yalnız etiket verir (enum'a Türkçe metin girmez, §10).
 */
export const KAPSAM_TIP_ETIKET: Record<RoleScopeType, string> = {
  company: 'Şirket',
  workplace: 'İşyeri',
  location_branch: 'Lokasyon dalı',
  contractor: 'Yüklenici firma',
};

/**
 * PTW etiketleri (F3·S2 — Kapsam §16.7). 11 token + 5 süper-durum: enum'a
 * Türkçe metin GİRMEZ (§10), ekran etiketi buradan gelir. Süper-durum
 * kolonu yoktur — token'dan türetilir (`ptwSuperDurumOf`), etiket ise
 * raporun/listenin sözü olduğu için burada durur.
 */
/**
 * ✏️ **K7.1 (tur 10) — HARİTA YAZILMAZ, IMPORT EDİLİR.** Gövde
 * `@isg/validation PTW_DURUM_ETIKETI`ye taşındı: sunucu ret mesajları da
 * (`InvalidPtwGecisError`) aynı adları basıyor ve iki kopya, birine eklenen
 * bir token'ın diğerinde ham kod olarak çıkmasıyla ayrışırdı (§2.31a'nın
 * ekran-cümlesi kolu). Ad burada DURUR — on ekran onu bu adla okur.
 */
export const PTW_DURUM_ETIKET: Record<string, string> = PTW_DURUM_ETIKETI;

export const PTW_SUPER_DURUM_ETIKET: Record<string, string> = PTW_SUPER_DURUM_ETIKETI;

export const PTW_ONAY_SONUC_ETIKET: Record<string, string> = {
  approved: 'Onayla',
  revision_requested: 'Revizyon iste',
  rejected: 'Reddet',
};

/** Onay kapısı (S0-K4): üç kapı AYNI zincirden koşar; listede hangi turun
 *  imzası beklendiği ancak bu etiketle okunur. */
/** Basamak durumu — izin sayfasının Onaylar bloğu (K-62): geçmiş zaman.
 *  `PTW_ONAY_SONUC_ETIKET` düğme (emir) dilidir; ikisi karışmaz. */
export const PTW_BASAMAK_DURUM_ETIKET: Record<string, string> = {
  bekliyor: 'Bekliyor',
  approved: 'Onaylandı',
  revision_requested: 'Revizyon istendi',
  rejected: 'Reddedildi',
  sifirlandi: 'Sıfırlandı',
};

export const PTW_ONAY_KAPI_ETIKET: Record<string, string> = {
  acilis: 'Açılış',
  uzatma: 'Uzatma',
  kapanis: 'Kapanış',
};

/** Geçiş log'unun işlem kodları (ERD §12/2 matris satırları) — kayda KOD
 *  yazılır, ekrana cümle çıkar (D2 iki sözlük ilkesinin iz kaydı kolu). */
export const PTW_ISLEM_ETIKET: Record<string, string> = PTW_ISLEM_ETIKETI;

/** Uygunsuzluk KURAL kodları (K8 üçlüsünün "hangi kural" kolonu). Kayda kod
 *  yazılır; iç yüzeyin tam cümlesi API'den gelir, bu etiket rozet içindir. */
// ✏️ DİLİM 2 (madde 235): harita EKSİKTİ (sağlık + T7 kodları yoktu) ve rozet
// iç token'ı basıyordu ("ilave_egitim_gerekli"). Tam harita
// `@isg/validation PTW_UYGUNLUK_KURAL_ETIKETI`de (Record — yeni kod etiketsiz
// derlenmez); burada yalnız YENİDEN DIŞA VERİLİR (K7.1 deseni).
export { PTW_UYGUNLUK_KURAL_ETIKETI as PTW_UYGUNLUK_KURAL_ETIKET } from '@isg/validation';

/**
 * EKİPMAN DURUMU — ekran ile Excel çıktısı **aynı kelimeyi** konuşur.
 * ⚠️ Geçiş geçmişi bu haritadan önce ham enum (`aktif → pasif`) basıyordu
 * (K9/D2): iç token'lar kullanıcı yüzeyine inmez.
 */
export const EKIPMAN_DURUM_ETIKETI: Record<'aktif' | 'pasif', string> = {
  aktif: 'Aktif',
  pasif: 'Pasif',
};

export const PTW_IZOLASYON_DURUM_ETIKET: Record<string, string> = {
  applied: 'Takıldı',
  verified: 'Doğrulandı',
  removed: 'Kaldırıldı',
};

export const PTW_GERI_CEKILME_KAYNAK_ETIKET: Record<string, string> = {
  manuel: 'Elle durdurma',
  ihlal_karari: 'İhlal kararı',
};

/** Eğitim modülü (F3B·S7 — Kapsam §16.10). Enum'a Türkçe metin GİRMEZ;
 *  ekranda görünen her etiket buradan gelir (web v1 TR). */
// ✏️ Tur 5 / Y3: etiketlerin TEK KAYNAĞI @isg/validation — PDF çıktısı ile
// ekran aynı kelimeyi kullansın (rapor etiketleri deseni). Burası alias.
export const EGITIM_YONTEM_ETIKET: Record<string, string> = EGITIM_YONTEM_ETIKETI;
export const EGITIM_TARAF_ETIKET: Record<string, string> = EGITIM_TARAF_ETIKETI;
export const EGITIM_SONUC_ETIKET: Record<string, string> = EGITIM_SONUC_ETIKETI;

// ── MENÜ + SEKME ETİKETLERİ (F4·S5·B1 — Kapsam §20.3a) ──────────────────────

/**
 * Etiketler TEK YERDE durur; menü modeli (`shell/menu.ts`) yalnız anahtar
 * taşır ve `data-testid` anahtardan kurulur (e2e etikete bağlı değildir).
 *
 * ✏️ **29.09.2026 — ETİKET ARTIK SIRA TAŞIMAZ.** *21.08.2026'da Operasyon
 * grubunun sırası bu metinlerden hesaplanıyordu (IA §3/6 — ad değişikliği
 * menü sırasını değiştirirdi).* Gruplar elle sıralı hâle geldi; satırın yeri
 * `menu.ts` dizisinin sırasıdır.
 *
 * Ad seti T7'de onaylandı: Kişiler → **Çalışanlar** · Ekipman → **İş
 * ekipmanları** · Organizasyon ağacı + Şirketler → **Organizasyon**.
 */
export const MENU_ETIKET: Record<string, string> = {
  'genel-bakis': 'Genel bakış',
  organizasyon: 'Organizasyon',
  calisanlar: 'Çalışanlar',
  yukleniciler: 'Yükleniciler',
  ekipman: 'İş ekipmanları',
  aksiyonlar: 'Aksiyonlar',
  egitimler: 'Eğitimler',
  formlar: 'Formlar',
  izinler: 'İş izinleri',
  olaylar: 'Olaylar',
  uygunsuzluklar: 'Uygunsuzluklar',
  risk: 'Risk değerlendirme',
  saglik: 'Sağlık gözetimi',
  'yasal-uygunluk': 'Yasal uygunluk',
  'saha-turlari': 'Saha turları',
  'isg-kurulu': 'İSG kurulu',
  'acil-durum': 'Acil durum',
  // Doküman yönetimi (Kapsam §31): *Doküman* grubunun ilk satırı, *Formlar*ın üstünde
  // (29.09.2026 — satırın yeri `menu.ts` dizisinin sırasıdır, etiketten HESAPLANMAZ).
  // ⚠️ K4: *Dokümanlar* (raf) ile *Formlar* (motor) ayrı satırlardır; açıklama cümlesi YAZILMAZ (M1).
  dokumanlar: 'Dokümanlar',
  yapilandirma: 'Yapılandırma',
  sozlukler: 'Sözlükler',
  kullanicilar: 'Kullanıcılar',
};

/**
 * Grup başlıkları (T4 ✏️ 29.09.2026 — beş grup, açılır-kapanır). Anahtarlar
 * `menu.ts MenuGrupAnahtari` ile birebirdir.
 */
export const MENU_GRUP_ETIKET: Record<string, string> = {
  kaynaklar: 'Kaynaklar',
  planlama: 'Planlama',
  performans: 'Performans',
  dokuman: 'Doküman',
  yonetim: 'Yönetim',
};

/** Modül içi sekme etiketleri (birleşen ekranlar — IA §4.1). */
export const SEKME_ETIKET: Record<string, string> = {
  // TUR 8 / C2 — takvim "ne gerekiyor", kütüphane "kural ne" der.
  'yasal-uygunluk-takvim': 'Takvim',
  'yasal-uygunluk-kutuphane': 'Kütüphane',
  'organizasyon-sirketler': 'Şirketler',
  'organizasyon-agac': 'Ağaç görünümü',
  'aksiyonlar-liste': 'Aksiyonlar',
  'izinler-liste': 'İzinler',
  'izinler-saha': 'Saha adımları',
  'izinler-zincirler': 'Onay zincirleri',
  'izinler-yapilandirma': 'Yapılandırma',
  'olaylar-liste': 'Olaylar',
  'olaylar-calisma-saatleri': 'Çalışma saatleri',
  'olaylar-siddet-seviyesi': 'Şiddet seviyesi',
  // F4·S5·B2 — beşinci motorun yüzeyi NESNESİNİN MODÜLÜNDE (T8): ekipman
  // görevleri İş ekipmanları'nda; menüde satır ARTMAZ.
  'ekipman-liste': 'Envanter',
  // ✏️ **E3 — ADLANDIRMA REVİZYONU (Tur 4½ / Aşama B, 03.09.2026).**
  // Ürün sahibi *"Görev tanımları"* ile *"Periyodik görevler"* farkını
  // ekrandan anlayamadı ve bu bir kullanıcı hatası değil, **adlandırma
  // hatasıydı**: iki ad da aynı kelimeyi (*görev*) aynı ağırlıkta kullanıp
  // aralarındaki **üretim ilişkisini** hiç söylemiyordu.
  //
  // Doğru ayrım: tanım bir **KURAL**dır (hangi ekipman · kim · hangi form ·
  // hangi periyot); periyodik görev o kuraldan **DOĞAN tekil iştir** (vadesi
  // gelince üretilir, form doldurulunca kapanır).
  //
  // ⚠️ **YALNIZ BİR YARISI DEĞİŞTİ.** *"Periyodik görevler"* zaten doğruydu
  // ve **iki adı birden değiştirmek** kullanıcının bildiği tek çapayı da
  // götürürdü. *"Kurallar"* tek başına ise fazla genel: sözlük kuralları ·
  // tenant ayarları · periyot kuralları da birer kuraldır.
  'ekipman-gorevler': 'Periyodik görevler',
  'ekipman-gorev-tanimlari': 'Görev kuralları',
  // Tur 4½ / B3 — görevlendirme listesi Çalışanlar modülünde (T8 deseni).
  // D2-1 (bulgu 92) — Formlar modülü iki sekme.
  'formlar-sablonlar': 'Şablonlar',
  'formlar-kayitlar': 'Dolu kayıtlar',
  'calisanlar-liste': 'Çalışanlar',
  'calisanlar-gorevlendirmeler': 'Görevlendirmeler',
  // KKD (Kapsam §30/8) — zimmet listesi Çalışanlar modülünde (T8 deseni; menüde satır artmaz).
  'calisanlar-kkd-zimmetleri': 'KKD zimmetleri',
  // DOKÜMAN RAFI (Kapsam §31/K1) — altı sekme, rota tabanlı (`/dokumanlar/:sekme`).
  'dokumanlar-plan': 'Plan',
  'dokumanlar-politika': 'Politika',
  'dokumanlar-prosedur': 'Prosedür',
  'dokumanlar-talimat': 'Talimat',
  'dokumanlar-form': 'Form',
  'dokumanlar-sema': 'Şema',
  'dokumanlar-diger': 'Diğer',
  // P2 (01.11.2026): sekme kısa adla — tipin tam adı "Güvenlik Bilgi Formu (GBF)" satır rozetinde.
  'dokumanlar-gbf': 'GBF',
  // Tur 4½ / B4 — sözlük bakımının iki yüzü.
  'sozluk-diger-kuyrugu': '“Diğer” kuyruğu',
  'sozluk-gorevlendirme-turleri': 'Görevlendirme türleri',
  // Tur 5 / İ2 + K13 — kalem kuralı kalem üstünde (4½ B4 deseni); genel
  // sözlük yönetimi ekranı 5½'te. Üç sekme, üç dar yüzey.
  'sozluk-egitim-turleri': 'Eğitim türleri',
  'sozluk-egitim-konulari': 'Eğitim konuları',
  'sozluk-muayene-turleri': 'Muayene türleri',
};

/** Üst bardaki kişisel kuyruk (T1b) — menüde satırı YOKTUR. */
export const ISLERIM_ETIKET = 'İşlerim';
/**
 * Dar bantta (§11.1 üçüncü basamak) *İşlerim* düğmesinin metni yerine geçen
 * işaret. ⚠️ **ETİKET KAYBOLMAZ, `aria-label`e TAŞINIR** — düğme görsel
 * olarak sadeleşir, erişilebilir adı aynı kalır. İşaret zil düğmesiyle aynı
 * aileden (bant işaretleri yazı tipinden gelir; ikon kütüphanesi YOK).
 */
export const ISLERIM_IKONU = '📋';

/**
 * Form ALAN TİPİ etiketleri (şablon düzenleyicisi). Liste ürün kararıdır
 * (`@isg/validation FORM_ALAN_TIPLERI`) — burada yalnız TR karşılığı durur;
 * enum'a Türkçe metin girmez (Teknoloji §10).
 */
export const FORM_ALAN_TIPI_ETIKET: Record<string, string> = {
  metin: 'Kısa metin',
  uzun_metin: 'Uzun metin',
  sayi: 'Sayı',
  tarih: 'Tarih',
  tekli_secim: 'Tekli seçim',
  coklu_secim: 'Çoklu seçim',
  evet_hayir: 'Evet / Hayır',
  dosya: 'Dosya',
  // Tur 3 (Ş2) — checklist maddesi: seçenekleri CEVAP SETİNDEN gelir.
  kontrol: 'Kontrol maddesi',
};

/**
 * G1 — madde bazında kanıt ayarı (fotoğraf / not); üç hâl (tur 3 artçısı).
 * ⚠️ *"Zorunlu"* ile *"İsteğe bağlı"* arasındaki farkı kullanıcı ancak
 * ETİKETTEN okur — enum token'ı ekrana çıkmaz.
 */
export const MADDE_KANIT_AYARI_ETIKET: Record<string, string> = {
  kapali: 'Kapalı',
  istege_bagli: 'İsteğe bağlı',
  zorunlu: 'Zorunlu',
};

/** Alanın kaynağı — ürün alanı silinemez, gizlenir (Kapsam §22.5/1). */
export const FORM_ALAN_KAYNAK_ETIKET: Record<string, string> = {
  urun: 'ürün',
  musteri: 'müşteri',
};

/**
 * 5½ / A1 — SÖZLÜK LİSTESİ ETİKETLERİ (genel sözlük yönetimi ekranı; Kapsam
 * §19/16). Sınıf/modül/editör bilgisi `@isg/validation SOZLUK_LISTE_ENVANTERI`
 * — burada yalnız görünen ad (tek dil katmanı). Tamlığı `Record`
 * tipiyle derleme kilitler: enum'a giren liste burada da satır ister.
 */
export const SOZLUK_LISTE_ETIKET: Record<LookupListeKodu, string> = {
  yetkinlik_tipi: 'Yetkinlik / belge tipleri',
  yetki_tipi: 'Yetki tipleri',
  gorevlendirme_turu: 'Görevlendirme türleri',
  equipment_tip: 'Ekipman tipleri',
  kapasite_birimi: 'Kapasite birimleri',
  evrak_tipi: 'Yüklenici evrak tipleri',
  olay_turu: 'Olay türleri',
  olay_sinifi: 'Olay sınıfları',
  olay_siddeti: 'Kişi şiddet merdiveni',
  mekanizma: 'Olay mekanizmaları',
  yayilim_siniri: 'Çevre yayılım sınırı',
  madde_tehlike_sinifi: 'Madde tehlike sınıfları',
  kok_sebep_grubu: 'Sebep kategorileri',
  kok_sebep: 'Sebep kalemleri',
  olay_red_gerekcesi: 'Olay red gerekçeleri',
  olay_kapanis_gerekcesi: 'Olay kapanış gerekçeleri (kullanım dışı)',
  olay_aydinlatma: 'Aydınlatma durumu',
  olay_zemin: 'Zemin durumu',
  yaralanma_turu: 'Yaralanma türleri',
  vucut_bolgesi: 'Vücut bölgeleri',
  aksiyon_kaynagi: 'Aksiyon kaynakları',
  izin_turu: 'İzin türleri',
  izin_gorevi: 'İzin içi görevler',
  geri_cekilme_gerekcesi: 'Geri çekilme gerekçeleri',
  egitim_turu: 'Eğitim türleri',
  egitim_konusu: 'Eğitim konuları',
  egitim_yeri: 'Eğitim yerleri (İBYS)',
  sertifika_tipi: 'Sertifika tipleri (İBYS)',
  egitim_yontemi_ibys: 'Eğitim yöntemleri (İBYS)',
  muayene_turu: 'Muayene türleri',
  uygunluk_karari: 'Uygunluk kararları',
  saglik_kisiti: 'Sağlık kısıtları',
  risk_tehlike: 'Risk tehlikeleri (kaynak)',
  risk_riski: 'Riskler (gerçekleşme)',
  denetlenen_birim: 'Denetlenen birimler',
  kkd_kalemi: 'KKD kalemleri',
};

export const SOZLUK_MODUL_ETIKET: Record<SozlukModulu, string> = {
  calisan: 'Çalışanlar',
  ekipman: 'İş ekipmanları',
  yuklenici: 'Yükleniciler',
  olay: 'Olaylar',
  aksiyon: 'Aksiyonlar',
  izin: 'İş izinleri',
  egitim: 'Eğitim',
  saglik: 'Sağlık gözetimi',
  risk: 'Risk değerlendirme',
  saha_turu: 'Saha turu / denetim',
  kkd: 'KKD zimmeti',
};

export const SOZLUK_SINIF_ETIKET: Record<SozlukListeSinifi, string> = {
  kutuphane: 'Kütüphane + müşteri kalemi',
  karisik: 'Kütüphane; ekleme bağla',
  musteri: 'Müşteri listesi',
  sabit: 'Ürün kilitli',
};

/** 5½ / B1 — çalışan temsilcisinin belirlenme yolu (Yönetmelik md. 20). */
export const BELIRLENME_YOLU_ETIKET: Record<BelirlenmeYolu, string> = {
  secim: 'Seçimle',
  atama: 'Atamayla',
};
